import type {
  BridgeMap,
  ThreadAsset,
  ThreadAssetSource,
  ThreadAssetType,
  ThreadCluster,
  ThreadColumn,
  ThreadFamily,
  ThreadHealth,
  ThreadRow,
} from "../thread-design-system";
import type {
  ProjectSummary,
  V3ProjectionCell,
  V3ProjectionRow,
  V3ProvenanceCluster,
  V3ProvenanceEdge,
  V3ProvenanceGraph,
  V3ProvenanceNode,
  V3ProvenanceNodeType,
  V3WorkbenchData,
} from "./types";
import { derivePivotCandidates } from "./pivots";

export type V3FixtureLevel = "low" | "med" | "high" | "tsangpo";

type FixtureColumn = {
  id: string;
  label: string;
  kind?: string;
  unit?: string;
};

type FixtureNode = {
  id: string;
  type: string;
  label: string;
  cluster: string;
  position?: { x: number; y: number };
  attrs?: Record<string, unknown>;
  health?: string | null;
};

type V3RawFixtureNode = Omit<V3ProvenanceNode, "type"> & { type: string };

type FixtureEdge = {
  id: string;
  from: string;
  to: string;
  type: string;
  attrs?: Record<string, unknown>;
};

type FixtureCluster = {
  id: string;
  label: string;
  type: string;
  position?: { x: number; y: number };
  radius?: number;
  totalCount?: number;
};

type FixtureAsset = {
  id: string;
  nodeId: string;
  type: string;
  source: string;
  title: string;
  summary?: string;
  health?: string;
  updated?: string;
};

type FixtureCompatibility = {
  from: string;
  to: string;
  state: string;
};

export type V3ProvenanceFixture = {
  detailLevel: V3FixtureLevel;
  project: ProjectSummary & { scale?: { totalNodes?: number; totalEdges?: number } };
  activeProjectionId: string;
  columns: FixtureColumn[];
  compatibility?: FixtureCompatibility[];
  clusters: FixtureCluster[];
  nodes: FixtureNode[];
  edges: FixtureEdge[];
  assets: FixtureAsset[];
};

const BASE_PATH = import.meta.env.BASE_URL.replace(/\/$/, "");
const FIXTURE_ROOT = `${BASE_PATH}/internal/thread-v3-provenance`;
const FIXTURE_LEVELS = new Set<V3FixtureLevel>(["low", "med", "high", "tsangpo"]);
const NUMERIC_COLUMNS = new Set(["alpha", "mach", "re", "reynolds", "cl", "cd", "cm", "ld"]);
const CONDITION_COLUMNS = new Set(["alpha", "mach", "re", "reynolds", "conditionKey", "condition_key"]);

export function normalizeV3FixtureLevel(value: string | null | undefined): V3FixtureLevel | null {
  return value && FIXTURE_LEVELS.has(value as V3FixtureLevel) ? value as V3FixtureLevel : null;
}

function health(value: unknown): ThreadHealth {
  if (value === "fresh" || value === "warn" || value === "stale" || value === "unknown") return value;
  return "unknown";
}

function columnKind(column: FixtureColumn): ThreadColumn["kind"] {
  if (column.kind === "id") return "id";
  if (column.kind === "quantity" || NUMERIC_COLUMNS.has(column.id)) return "quantity";
  if (column.id === "modelFamily") return "family";
  if (column.kind === "condition" || CONDITION_COLUMNS.has(column.id)) return "condition";
  return "metadata";
}

function primitive(value: unknown): string | number | null | undefined {
  return typeof value === "string" || typeof value === "number" || value == null ? value : undefined;
}

function assetType(value: string): ThreadAssetType {
  const type = value.toLowerCase();
  if (type.includes("presentation") || type.includes("deck") || type.includes("slide")) return "deck";
  if (type.includes("spreadsheet") || type.includes("sheet") || type.includes("workbook")) return "spreadsheet";
  if (type.includes("chat") || type.includes("slack")) return "chat";
  if (type.includes("email")) return "email";
  if (type.includes("uvf") || type.includes("flowfield")) return "uvf";
  if (type.includes("notebook")) return "notebook";
  if (type.includes("decision")) return "decision";
  if (type.includes("storyboard")) return "storyboard";
  if (type.includes("plot")) return "plot";
  return "report";
}

function assetSource(value: string): ThreadAssetSource {
  const source = value.toLowerCase();
  if (source.includes("powerpoint")) return "powerpoint";
  if (source.includes("google-slides")) return "google-slides";
  if (source.includes("sharepoint")) return "sharepoint";
  if (source.includes("google-sheets")) return "google-sheets";
  if (source.includes("excel")) return "excel";
  if (source.includes("slack")) return "slack";
  if (source.includes("email") || source.includes("outlook")) return "email";
  if (source.includes("pdf")) return "pdf";
  if (source.includes("uvf")) return "uvf";
  if (source.includes("runway")) return "runway";
  return "thread";
}

function familyLabel(id: string): string {
  return id.replace(/-/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
}

function buildIndexes(nodes: V3ProvenanceNode[], edges: V3ProvenanceEdge[]) {
  const nodesById = new Map(nodes.map((node) => [node.id, node]));
  const incomingByNode = new Map<string, V3ProvenanceEdge[]>();
  const outgoingByNode = new Map<string, V3ProvenanceEdge[]>();
  for (const edge of edges) {
    if (!incomingByNode.has(edge.to)) incomingByNode.set(edge.to, []);
    if (!outgoingByNode.has(edge.from)) outgoingByNode.set(edge.from, []);
    incomingByNode.get(edge.to)!.push(edge);
    outgoingByNode.get(edge.from)!.push(edge);
  }
  return { nodesById, incomingByNode, outgoingByNode };
}

function walkIds(startId: string, edgesByNode: Map<string, V3ProvenanceEdge[]>, direction: "up" | "down", limit = 900): Set<string> {
  const seen = new Set<string>([startId]);
  const queue = [startId];
  while (queue.length && seen.size < limit) {
    const id = queue.shift()!;
    for (const edge of edgesByNode.get(id) ?? []) {
      const next = direction === "up" ? edge.from : edge.to;
      if (seen.has(next)) continue;
      seen.add(next);
      queue.push(next);
    }
  }
  return seen;
}

function isFixtureProjectionType(type: string): boolean {
  return type === "table_projection" || type === "table_row_projection" || type === "table_cell_projection";
}

function buildRows(nodes: V3RawFixtureNode[]): { rows: ThreadRow[]; projectionRows: V3ProjectionRow[] } {
  const rowNodes = nodes.filter((node) => node.type === "table_row_projection");
  const cellNodesByRow = new Map<string, V3RawFixtureNode[]>();
  for (const node of nodes) {
    if (node.type !== "table_cell_projection") continue;
    const rowNodeId = typeof node.attrs?.rowNodeId === "string" ? node.attrs.rowNodeId : "";
    if (!cellNodesByRow.has(rowNodeId)) cellNodesByRow.set(rowNodeId, []);
    cellNodesByRow.get(rowNodeId)!.push(node);
  }

  const rows: ThreadRow[] = [];
  const projectionRows: V3ProjectionRow[] = [];
  for (const rowNode of rowNodes) {
    const runId = typeof rowNode.attrs?.runId === "string" ? rowNode.attrs.runId : rowNode.label;
    const family = typeof rowNode.attrs?.modelFamily === "string" ? rowNode.attrs.modelFamily : "unknown";
    const row: ThreadRow = {
      runId,
      rowNodeId: rowNode.id,
      cellNodeIds: {},
      family,
      conditionKey: typeof rowNode.attrs?.conditionNodeId === "string" ? rowNode.attrs.conditionNodeId : undefined,
      status: health(rowNode.health),
    };
    const projectionCells: V3ProjectionCell[] = [];

    for (const cell of cellNodesByRow.get(rowNode.id) ?? []) {
      const colId = typeof cell.attrs?.colId === "string" ? cell.attrs.colId : "";
      if (!colId) continue;
      const value = primitive(cell.attrs?.value);
      row.cellNodeIds![colId] = cell.id;
      row[colId] = value;
      if (colId === "modelFamily" && typeof rowNode.attrs?.modelFamily !== "string" && typeof value === "string") row.family = value;
      if (colId === "geometry" && typeof value === "string") row.design = value;
      if (colId === "mesh" && typeof value === "string") row.mesh = value;
      if (colId === "correction" && typeof value === "string") row.script = value;
      if (colId === "reviewState") row.status = health(value);
      if (colId === "reynolds" && typeof value === "number") row.re = value;
      if ((colId === "alpha" || colId === "mach") && typeof value === "number") row[colId] = value;
      projectionCells.push({
        handleId: cell.id,
        rowHandleId: rowNode.id,
        rowId: runId,
        colId,
        sourceNodeIds: typeof cell.attrs?.sourceNodeId === "string" ? [cell.attrs.sourceNodeId] : [],
        value,
      });
    }

    rows.push(row);
    projectionRows.push({
      handleId: rowNode.id,
      runId,
      sourceNodeIds: projectionCells.flatMap((cell) => cell.sourceNodeIds),
      cellHandleIds: { ...row.cellNodeIds },
      cells: projectionCells,
    });
  }

  return { rows, projectionRows };
}

function buildFamilies(rows: readonly ThreadRow[]): Record<string, ThreadFamily> {
  const families: Record<string, ThreadFamily> = {};
  for (const row of rows) {
    if (families[row.family]) continue;
    families[row.family] = { label: familyLabel(row.family), short: row.family.slice(0, 8).toUpperCase(), tone: "fresh" };
  }
  return families;
}

function buildBridges(fixture: V3ProvenanceFixture): BridgeMap {
  const bridgePairs = (fixture.compatibility ?? [])
    .filter((item) => item.state === "compatible" || item.state === "connected")
    .map((item) => [item.from, item.to] as const);
  return bridgePairs.length ? { cl: bridgePairs, cd: bridgePairs, cm: bridgePairs, ld: bridgePairs } : {};
}

function buildAssets(fixture: V3ProvenanceFixture, graph: V3ProvenanceGraph, projectionRows: readonly V3ProjectionRow[]): ThreadAsset[] {
  return fixture.assets.map((asset) => {
    const upstream = walkIds(asset.nodeId, graph.incomingByNode, "up");
    const downstream = walkIds(asset.nodeId, graph.outgoingByNode, "down");
    const rowRefs = new Set<string>();
    const cellRefs: { runId: string; col: string }[] = [];
    for (const row of projectionRows) {
      const rowTouched = row.sourceNodeIds.some((nodeId) => upstream.has(nodeId));
      if (rowTouched) rowRefs.add(row.runId);
      for (const cell of row.cells) {
        if (cell.sourceNodeIds.some((nodeId) => upstream.has(nodeId))) {
          rowRefs.add(row.runId);
          cellRefs.push({ runId: row.runId, col: cell.colId });
        }
      }
    }
    const type = assetType(asset.type);
    return {
      id: asset.id,
      provenanceNodeId: asset.nodeId,
      childNodeIds: [...downstream].filter((id) => id !== asset.nodeId),
      type,
      source: assetSource(asset.source),
      label: asset.title,
      sub: `${asset.source} · ${fixture.detailLevel} fixture`,
      updated: asset.updated ?? "fixture",
      health: health(asset.health),
      summary: asset.summary,
      refs: { rows: [...rowRefs], cells: cellRefs, upstream: [...upstream] },
      preview:
        type === "plot"
          ? { kind: "plot", seriesCount: Math.max(1, [...downstream].filter((id) => graph.nodesById.get(id)?.type === "plot_trace").length) }
          : type === "deck"
            ? { kind: "deck", slideTitle: asset.title, slideIndex: 14 }
            : undefined,
    };
  });
}

function clusterEdges(edges: readonly V3ProvenanceEdge[], nodesById: Map<string, V3ProvenanceNode>): Array<readonly [string, string]> {
  const pairs = new Set<string>();
  for (const edge of edges) {
    const from = nodesById.get(edge.from)?.cluster;
    const to = nodesById.get(edge.to)?.cluster;
    if (from && to && from !== to) pairs.add(`${from}|${to}`);
  }
  return [...pairs].map((pair) => pair.split("|") as [string, string]);
}

export function buildV3FixtureWorkbenchData(fixture: V3ProvenanceFixture, errors: string[] = []): V3WorkbenchData {
  const columns = fixture.columns.map((column): ThreadColumn => ({
    id: column.id,
    label: column.label,
    kind: columnKind(column),
    units: column.unit,
  }));
  const rawNodes = fixture.nodes.map((node): V3RawFixtureNode => ({
    id: node.id,
    type: node.type,
    label: node.label,
    cluster: node.cluster,
    position: node.position ?? { x: 0, y: 0 },
    attrs: node.attrs,
    health: node.health,
  }));
  const { rows, projectionRows } = buildRows(rawNodes);
  const rawNodesById = new Map(rawNodes.map((node) => [node.id, node]));
  const sourceIdsForProjectionNode = (nodeId: string): string[] => {
    const node = rawNodesById.get(nodeId);
    if (!node) return [];
    if (node.type === "table_cell_projection") {
      return typeof node.attrs?.sourceNodeId === "string" ? [node.attrs.sourceNodeId] : [];
    }
    if (node.type === "table_row_projection") {
      return projectionRows.find((row) => row.handleId === nodeId)?.sourceNodeIds ?? [];
    }
    return [];
  };
  const nodes = rawNodes
    .filter((node) => !isFixtureProjectionType(node.type))
    .map((node): V3ProvenanceNode => ({
      ...node,
      type: (node.type === "condition" ? "parameter_atom" : node.type) as V3ProvenanceNodeType,
      cluster: node.type === "condition" ? "run" : node.cluster,
    }));
  const edges = fixture.edges.flatMap((edge): V3ProvenanceEdge[] => {
    const fromNode = rawNodesById.get(edge.from);
    const toNode = rawNodesById.get(edge.to);
    const fromProjection = Boolean(fromNode && isFixtureProjectionType(fromNode.type));
    const toProjection = Boolean(toNode && isFixtureProjectionType(toNode.type));
    if (toProjection) return [];
    if (fromProjection) {
      return sourceIdsForProjectionNode(edge.from).map((sourceNodeId, index) => ({
        id: `${edge.id}:source:${index}`,
        from: sourceNodeId,
        to: edge.to,
        type: edge.type === "consumed_by" ? "source_consumed_by" : edge.type,
        attrs: edge.attrs,
      }));
    }
    return [{
      id: edge.id,
      from: edge.from,
      to: edge.to,
      type: edge.type,
      attrs: edge.attrs,
    }];
  }).filter((edge) => rawNodesById.has(edge.from) && rawNodesById.has(edge.to) && !isFixtureProjectionType(rawNodesById.get(edge.from)?.type ?? "") && !isFixtureProjectionType(rawNodesById.get(edge.to)?.type ?? ""));
  const clusters = fixture.clusters.filter((cluster) => !cluster.id.toLowerCase().includes("table") && !cluster.type.toLowerCase().includes("table")).map((cluster): V3ProvenanceCluster => ({
    id: cluster.id,
    label: cluster.label,
    type: cluster.type,
    position: cluster.position ?? { x: 0, y: 0 },
    radius: cluster.radius ?? 64,
    totalCount: cluster.totalCount ?? nodes.filter((node) => node.cluster === cluster.id).length,
  }));
  const threadClusters: ThreadCluster[] = clusters.map((cluster) => ({
    id: cluster.id,
    type: cluster.type,
    label: cluster.label,
    nodes: cluster.totalCount,
    x: cluster.position.x,
    y: cluster.position.y,
    health: "fresh",
  }));
  const indexes = buildIndexes(nodes, edges);
  const cellsByRowCol = new Map<string, V3ProjectionCell>();
  for (const row of projectionRows) {
    for (const cell of row.cells) cellsByRowCol.set(`${row.runId}:${cell.colId}`, cell);
  }
  const assetNodeIdByAssetId = new Map(fixture.assets.map((asset) => [asset.id, asset.nodeId]));
  const provenance: V3ProvenanceGraph = {
    projectionId: fixture.activeProjectionId,
    nodes,
    edges,
    clusters,
    rows: projectionRows,
    nodesById: indexes.nodesById,
    incomingByNode: indexes.incomingByNode,
    outgoingByNode: indexes.outgoingByNode,
    cellsByRowCol,
    assetNodeIdByAssetId,
    representedScale: {
      totalNodes: fixture.project.scale?.totalNodes ?? nodes.length,
      totalEdges: fixture.project.scale?.totalEdges ?? edges.length,
    },
    source: "fixture",
  };
  const assets = buildAssets(fixture, provenance, projectionRows);
  const pivotViews = derivePivotCandidates(rows, columns, provenance);
  return {
    project: fixture.project,
    branch: `${fixture.detailLevel}-fixture`,
    rows,
    totalRowCount: rows.length,
    columns,
    families: buildFamilies(rows),
    bridges: buildBridges(fixture),
    assets,
    clusters: threadClusters,
    graphEdges: clusterEdges(edges, provenance.nodesById),
    provenance,
    pivotViews,
    source: "fixture",
    errors,
  };
}

export async function loadV3Fixture(level: V3FixtureLevel, signal?: AbortSignal, errors: string[] = []): Promise<V3WorkbenchData> {
  const response = await fetch(`${FIXTURE_ROOT}/thread-provenance-${level}.json`, {
    signal,
    headers: { "Content-Type": "application/json" },
  });
  if (!response.ok) throw new Error(`${response.status}: ${await response.text()}`);
  return buildV3FixtureWorkbenchData(await response.json() as V3ProvenanceFixture, errors);
}
